import type {
  NavigationState,
  NavigationStateRecord,
  NavigationStateSubRecord,
} from "@huub-web/ui";

/**
 * Screen types that can be rendered for a route.
 */
export type ScreenType =
  | "templates"
  | "advisor"
  | "history"
  | "interior-designer";

/**
 * Configuration for a single route derived from navigationState.
 */
export type RouteConfig = {
  /** The URL path pattern (React Router format with :param for dynamic segments) */
  path: string;
  /** The type of screen to render */
  screenType: ScreenType;
  /** The API endpoint to fetch templates from (if applicable) */
  endpoint?: string;
  /** The navigation context keys for this route */
  navigationContext: string[];
};

/**
 * Maps navigation context IDs to specific screen types.
 * Routes not in this map default to "templates" screen type.
 */
const SCREEN_TYPE_MAP: Record<string, ScreenType> = {
  advisor: "advisor",
  chatHistory: "history",
  interiorDesigner: "interior-designer",
};

/**
 * Converts a slug with bracket notation to React Router param format.
 * @example "[id]" -> ":id"
 * @example "some-path" -> "some-path"
 */
const convertSlugToRouterParam = (slug: string): string => {
  return slug.replace(/\[([^\]]+)\]/g, ":$1");
};

/**
 * Generates route configurations from backend navigationState.
 * Recursively processes the navigation tree to create flat route configs.
 *
 * @param navigationState - The navigation state from the backend
 * @returns Array of route configurations for React Router
 */
export const generateRoutesFromNavigationState = (
  navigationState: NavigationState,
): RouteConfig[] => {
  const routes: RouteConfig[] = [];

  /**
   * Recursively processes a navigation record and its children.
   * Child routes inherit the parent's screen type if they don't have their own mapping.
   */
  const processRecord = (
    record: NavigationStateRecord | NavigationStateSubRecord,
    parentPath: string = "",
    parentContext: string[] = [],
    parentScreenType: ScreenType = "templates",
  ): void => {
    // Build the full path for this record
    const normalizedSlug = record.slug.startsWith("/")
      ? record.slug.slice(1)
      : record.slug;
    const routerSlug = convertSlugToRouterParam(normalizedSlug);
    const path =
      parentPath === "/" ? `/${routerSlug}` : `${parentPath}/${routerSlug}`;

    // Build the navigation context
    const navigationContext = [...parentContext, record.id];

    // Determine the screen type - inherit from parent if not explicitly mapped
    const screenType: ScreenType =
      SCREEN_TYPE_MAP[record.id] ?? parentScreenType;

    // Add the route config
    routes.push({
      path: path === "//" ? "/" : path,
      screenType,
      endpoint: record.endpoint,
      navigationContext,
    });

    // Process child steps recursively, passing current screenType for inheritance
    if (record.steps && record.steps.length > 0) {
      for (const step of record.steps) {
        processRecord(step, path, navigationContext, screenType);
      }
    }
  };

  // Process all top-level navigation records
  for (const record of navigationState) {
    // Handle root path specially
    if (record.slug === "/") {
      const screenType = SCREEN_TYPE_MAP[record.id] ?? "templates";
      routes.push({
        path: "/",
        screenType,
        endpoint: record.endpoint,
        navigationContext: [record.id],
      });

      // Process children of root, passing screenType for inheritance
      if (record.steps && record.steps.length > 0) {
        for (const step of record.steps) {
          processRecord(step, "", [record.id], screenType);
        }
      }
    } else {
      processRecord(record, "", []);
    }
  }

  return routes;
};

/**
 * Gets the route config for a specific navigation context.
 *
 * @param routes - The generated route configs
 * @param context - The navigation context to match
 * @returns The matching route config or undefined
 */
export const getRouteConfigByContext = (
  routes: RouteConfig[],
  context: string[],
): RouteConfig | undefined => {
  return routes.find(
    (route) => route.navigationContext.join(".") === context.join("."),
  );
};
